import { useCallback, useEffect, useRef, useState } from "react";
import { ToolWithSidebar } from "@/components/ToolWithSidebar";
import { FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { AlertTriangle, Info } from "lucide-react";
import { Link } from "wouter";
import { trackToolCompleted } from "@/lib/analytics";
import { formatFileSize } from "@/lib/formatFileSize";
import { plural } from "@/lib/plural";
import { useRunToken } from "@/hooks/useRunToken";
import { loadPdfDocument } from "./utils";
import { stemOfPdfName } from "./pdfMetadata";
import { OCR_LANGUAGES } from "./ocrText";
import { LOCKED_PDF_MESSAGE, isPasswordError } from "./lockedPdfError";
import { ExcelConversionCancelled, convertPdfToExcelDocument, DEFAULT_RUN_OPTIONS, type ExcelOutcome, type ExcelRunOptions } from "./pdfToExcel";
import type { DateOrder } from "./cellValue";
import type { SheetMode } from "./pdfTables";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Finds the tables in a PDF and writes them to an Excel workbook you can actually calculate with. Numbers, currency, percentages and dates are written as real values (not text), so SUM, sorting and filters work; a table that carries on over a page break becomes one table, with the repeated heading row dropped; columns of right-aligned amounts stay in one column. Only the tables are written, not the paragraphs around them.\n\nGoing the other direction? Excel to PDF converts spreadsheet data into a PDF table.`,
  how: `Drop a PDF, choose how the sheets should be arranged (one per table, one per page, or everything on one sheet), and click Extract Tables. You get a count of what was found, a preview of the first rows of every sheet, and the .xlsx to download. Scanned pages are read with OCR first, which downloads the OCR engine and language data the first time. Your PDF itself is never uploaded; it is processed in your browser.`,
  tip: `A table is a run of at least three rows whose columns line up, so a table with only one or two rows, or one with merged cells or text wrapped over several lines inside a cell, may come out as separate rows or not be found; check the preview. Dates like 09/02/2026 can be day-first or month-first: the tool works it out from the other dates in the column, and when it cannot it leaves them as text until you choose the order. Codes, ZIP codes and numbers with leading zeros are kept as text on purpose. Scanned tables depend on scan quality, so check numbers read with OCR against the original. Charts and images in the PDF are not carried over.`,
};

const SHEET_MODES: { id: SheetMode; label: string }[] = [
  { id: "table", label: "One sheet per table" },
  { id: "page", label: "One sheet per page" },
  { id: "single", label: "Everything on one sheet" },
];

const DATE_ORDERS: { id: DateOrder; label: string }[] = [
  { id: "auto", label: "Work it out from the file" },
  { id: "dmy", label: "Day first (31/12/2026)" },
  { id: "mdy", label: "Month first (12/31/2026)" },
];

type Result = { outcome: ExcelOutcome; forFile: string };

export default function PdfToExcel() {
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<Result | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [options, setOptions] = useState<ExcelRunOptions>(DEFAULT_RUN_OPTIONS);
  const runs = useRunToken();
  const workerRef = useRef<{ terminate: () => Promise<unknown> } | null>(null);

  const killWorker = useCallback(() => {
    const w = workerRef.current;
    workerRef.current = null;
    if (w) void w.terminate().catch(() => undefined);
  }, []);

  useEffect(
    () => () => {
      runs.cancel();
      killWorker();
    },
    [runs, killWorker],
  );

  const setOption = <K extends keyof ExcelRunOptions>(key: K, value: ExcelRunOptions[K]) => {
    setOptions((o) => ({ ...o, [key]: value }));
    setResult(null); // the result on screen was made with the old settings
  };

  const handleFiles = useCallback(
    (files: File[]) => {
      runs.cancel();
      killWorker();
      setProcessing(false);
      setFile(files[0] || null);
      setResult(null);
      setErrorMessage("");
    },
    [runs, killWorker],
  );

  const reset = () => {
    runs.cancel();
    killWorker();
    setProcessing(false);
    setFile(null);
    setResult(null);
    setErrorMessage("");
    setProgress(0);
    setStatusMessage("");
  };

  const handleStop = () => {
    runs.cancel();
    killWorker();
    setProcessing(false);
    setStatusMessage("");
    setProgress(0);
  };

  const convert = async () => {
    if (!file) return;
    const run = runs.start();
    setProcessing(true);
    setResult(null);
    setErrorMessage("");
    setProgress(0);
    setStatusMessage("Opening the PDF…");
    try {
      const pdf = await loadPdfDocument(file);
      if (!runs.isCurrent(run)) return;
      const outcome = await convertPdfToExcelDocument(pdf, options, {
        isCurrent: () => runs.isCurrent(run),
        status: (m) => runs.isCurrent(run) && setStatusMessage(m),
        progress: (f) => runs.isCurrent(run) && setProgress(f),
        registerWorker: (w) => {
          if (runs.isCurrent(run)) workerRef.current = w;
        },
      });
      if (!runs.isCurrent(run)) return;
      setResult({ outcome, forFile: file.name });
      if (outcome.kind === "ok") {
        toast.success(`Found ${plural(outcome.summary.tables, "table")}`);
        trackToolCompleted("pdf-to-excel", "pdf-tools");
      }
    } catch (error) {
      if (error instanceof ExcelConversionCancelled || !runs.isCurrent(run)) return;
      console.error(error);
      if (isPasswordError(error)) {
        setErrorMessage(LOCKED_PDF_MESSAGE);
        return;
      }
      const detail = error instanceof Error && error.message ? error.message : "";
      setErrorMessage(detail && !/Invalid PDF|password/i.test(detail) ? `Extraction failed: ${detail}` : "Extraction failed. The PDF may be damaged or password-protected.");
    } finally {
      if (runs.isCurrent(run)) {
        setProcessing(false);
        setStatusMessage("");
      }
    }
  };

  const outcome = result?.outcome;
  const s = outcome?.summary;

  return (
    <ToolWithSidebar toolId="pdf-to-excel" categoryId="pdf-tools" whatItDoes={TOOL_INFO_CONTENT.what} proTip={TOOL_INFO_CONTENT.tip}>
      {!file ? (
        <FileDropZone gateAtDownload accept=".pdf" onFiles={handleFiles} label="Drop a PDF with tables" description="Extract table data to Excel — processed entirely in your browser" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm font-medium break-all">{file.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{formatFileSize(file.size)}</p>
          </div>

          {processing ? (
            <ProcessingState message={statusMessage || "Working…"} onStop={handleStop} progress={progress > 0 ? progress * 100 : undefined} />
          ) : (
            <>
              <fieldset className="space-y-3 rounded-lg border p-3 min-w-0">
                <legend className="px-1 text-sm font-medium">Options</legend>
                <div>
                  <p className="mb-1 text-sm">Sheets</p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="How to arrange the sheets">
                    {SHEET_MODES.map((m) => (
                      <Button key={m.id} type="button" size="sm" variant={options.sheetMode === m.id ? "default" : "outline"} aria-pressed={options.sheetMode === m.id} onClick={() => setOption("sheetMode", m.id)}>
                        {m.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={options.joinContinued && options.sheetMode !== "page"} disabled={options.sheetMode === "page"} onChange={(e) => setOption("joinContinued", e.target.checked)} />
                  Join a table that continues on the next page
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={options.convertValues} onChange={(e) => setOption("convertValues", e.target.checked)} />
                  Write numbers and dates as real values (so they can be summed and sorted)
                </label>
                {options.convertValues && (
                  <div className="flex flex-wrap items-center gap-2 text-sm pl-6">
                    <label htmlFor="date-order" className="text-muted-foreground">Dates like 09/02/2026 are:</label>
                    <select id="date-order" value={options.dateOrder} onChange={(e) => setOption("dateOrder", e.target.value as DateOrder)} className="h-9 rounded-md border bg-background px-2 text-sm">
                      {DATE_ORDERS.map((d) => (
                        <option key={d.id} value={d.id}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={options.ocr} onChange={(e) => setOption("ocr", e.target.checked)} />
                  Read scanned pages with OCR (pages with no selectable text)
                </label>
                {options.ocr && (
                  <div className="flex flex-wrap items-center gap-2 text-sm pl-6">
                    <label htmlFor="ocr-language" className="text-muted-foreground">Language of scanned pages:</label>
                    <select id="ocr-language" value={options.ocrLanguage} onChange={(e) => setOption("ocrLanguage", e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm">
                      {OCR_LANGUAGES.map((l) => (
                        <option key={l.id} value={l.id}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </fieldset>

              {errorMessage && (
                <Card className="p-3 border-red-300 bg-red-50 dark:bg-red-950/30" role="alert">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {errorMessage}
                      {errorMessage === LOCKED_PDF_MESSAGE && (
                        <> <Link href="/tool/unlock-pdf" className="underline">Open Unlock PDF</Link>.</>
                      )}
                    </p>
                  </div>
                </Card>
              )}

              {outcome?.kind === "none" && s && (
                <Card className="p-4 space-y-2" role="status">
                  <p className="text-sm font-medium">{outcome.reason === "no-text" ? "No readable text was found in this PDF." : "No tables were found in this PDF."}</p>
                  <p className="text-sm text-muted-foreground">
                    {outcome.reason === "no-text"
                      ? options.ocr
                        ? "The pages look like pictures and OCR could not read any text from them. A clearer scan may help, and the language setting must match the document."
                        : "The pages have no selectable text (they look like scans). Turn on “Read scanned pages with OCR” above and extract again."
                      : `Looked at ${plural(s.pages, "page")}. A table needs at least three rows whose columns line up; ordinary paragraphs, lists and two-column text are not treated as tables.`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    For a document that is mostly text, try <Link href="/tool/pdf-to-word" className="underline text-primary">PDF to Word</Link> or <Link href="/tool/extract-pdf" className="underline text-primary">Extract Pages &amp; Text</Link>.
                  </p>
                </Card>
              )}

              {outcome?.kind === "ok" && s ? (
                <div className="space-y-4">
                  <Card className="p-4 space-y-2" aria-live="polite">
                    <p className="text-sm font-medium">
                      {plural(s.tables, "table")} found · {plural(s.rows, "row")} in {plural(s.sheets, "sheet")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.numericCells > 0 || s.dateCells > 0
                        ? `${plural(s.numericCells, "number")}${s.dateCells ? ` and ${plural(s.dateCells, "date")}` : ""} written as real values.`
                        : options.convertValues
                          ? "No numeric or date columns were recognised, so everything is text."
                          : "Everything was written as text, as chosen."}
                    </p>
                    {s.joined.map(([a, b]) => (
                      <p key={`${a}-${b}`} className="text-xs text-muted-foreground">A table that continues over pages {a}–{b} was joined into one.</p>
                    ))}
                    {s.notes.map((n) => (
                      <NoticeBox key={n} className="text-xs flex gap-1.5">
                        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        {n}
                      </NoticeBox>
                    ))}
                  </Card>
                  <div className="flex flex-wrap gap-3">
                    <DownloadButton blob={outcome.blob} fileName={`${stemOfPdfName(result!.forFile)}.xlsx`} label="Download Excel File" />
                    <Button variant="outline" onClick={reset}>Convert Another</Button>
                  </div>
                  <div className="space-y-3" aria-label="Preview of the workbook">
                    {outcome.preview.map((sheet) => (
                      <details key={sheet.name} open={outcome.preview.length <= 3} className="rounded-lg border p-3">
                        <summary className="cursor-pointer text-sm font-medium">
                          {sheet.name} — {plural(sheet.rowCount, "row")} × {plural(sheet.columnCount, "column")}
                          {sheet.tableCount > 1 ? ` (${sheet.tableCount} tables)` : ""}
                        </summary>
                        <div className="mt-2 overflow-x-auto">
                          <table className="text-xs border-collapse">
                            <tbody>
                              {sheet.rows.map((row, ri) => (
                                <tr key={ri} className={ri < sheet.headerRows ? "font-semibold bg-muted/50" : ""}>
                                  {row.map((c, ci) => (
                                    <td key={ci} className={`border px-1.5 py-0.5 whitespace-nowrap ${c.numeric ? "text-right tabular-nums" : ""}`}>{c.text}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {sheet.rowCount > sheet.rows.length && <p className="mt-1 text-xs text-muted-foreground">Showing the first {sheet.rows.length} of {sheet.rowCount} rows.</p>}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Button onClick={convert}>Extract Tables</Button>
                  <Button variant="outline" onClick={reset}>Reset</Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </ToolWithSidebar>
  );
}
